import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Lock, LogOut, Loader2, CalendarCheck, Inbox, RefreshCw, FileSearch, ChevronDown, Trash2, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ra-admin-token";

const formatErr = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const fieldClass =
  "w-full rounded-xl bg-[rgba(8,12,22,0.7)] border border-[var(--line)] px-4 py-3 text-[var(--white)] placeholder:text-[var(--muted-2)] text-sm outline-none transition-colors duration-300 focus:border-[var(--blue)] focus:ring-1 focus:ring-[var(--blue)]";

const StatCard = ({ icon: Icon, label, value, tone, testId }) => (
  <div className="glass-card depth-card sheen rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{label}</span>
      <span className={`grid place-items-center h-9 w-9 rounded-lg ${tone === "green" ? "bg-[rgba(54,226,160,0.1)] text-[var(--green)]" : "bg-[rgba(61,123,255,0.1)] text-[var(--blue-bright)]"}`}>
        <Icon size={17} />
      </span>
    </div>
    <div className="mt-4 font-display text-4xl font-bold text-white">{value}</div>
  </div>
);

const AUDIT_STATUSES = ["new", "reviewed", "qualified", "call_scheduled", "proposal_needed", "closed_won", "closed_lost", "spam"];
const STATUS_LABEL = {
  new: "New", reviewed: "Reviewed", qualified: "Qualified", call_scheduled: "Call scheduled",
  proposal_needed: "Proposal needed", closed_won: "Closed won", closed_lost: "Closed lost", spam: "Spam",
};
const STATUS_TONE = {
  new: "text-[var(--blue-bright)] border-[var(--line-strong)]",
  reviewed: "text-[var(--blue-bright)] border-[var(--line-strong)]",
  qualified: "text-[var(--green)] border-[rgba(54,226,160,0.45)]",
  call_scheduled: "text-[var(--green)] border-[rgba(54,226,160,0.45)]",
  proposal_needed: "text-[#f0b34e] border-[rgba(240,179,78,0.45)]",
  closed_won: "text-[var(--green)] border-[rgba(54,226,160,0.6)]",
  closed_lost: "text-[var(--muted-2)] border-[var(--line)]",
  spam: "text-[var(--red)] border-[rgba(255,92,108,0.45)]",
};
const SYS_LABEL = {
  excel_spreadsheet: "Excel spreadsheet", pricing_calculator: "Pricing calculator", product_configurator: "Product configurator",
  quote_tool: "Quote tool", manual_workflow: "Manual workflow", ai_workflow_automation: "AI / workflow automation", other: "Other",
};
const CRIT_LABEL = { nice_to_have: "Nice to have", important: "Important", critical: "Critical" };
const BUDGET_LABEL = { below_25k: "<25k DKK", "25k_75k": "25-75k DKK", "75k_200k": "75-200k DKK", "200k_plus": "200k+ DKK", not_sure: "Not sure" };

const AuditRequests = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [busyId, setBusyId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/audit-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data || []);
    } catch (e) {
      /* handled by parent session check */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const patch = async (id, body) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/admin/audit-requests/${id}`, body, { headers });
      setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
      return true;
    } catch (e) {
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const addNote = async (id) => {
    if (!noteDraft.trim()) return;
    const ok = await patch(id, { note: noteDraft.trim() });
    if (ok) setNoteDraft("");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this audit request? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/audit-requests/${id}`, { headers });
      setItems((arr) => arr.filter((it) => it.id !== id));
      if (openId === id) setOpenId(null);
    } catch (e) {
      /* ignore */
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="glass-card rounded-3xl p-6 mt-5" data-testid="admin-audit-section">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">Secure System Audit Requests ({items.length})</div>
        <button onClick={load} className="btn-ghost !py-2 !px-3 text-xs" data-testid="audit-refresh-btn">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse" data-testid="admin-audit-table">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">
              <th className="py-3 pr-4 font-medium whitespace-nowrap">Date</th>
              <th className="py-3 pr-4 font-medium">Name / Company</th>
              <th className="py-3 pr-4 font-medium">Email</th>
              <th className="py-3 pr-4 font-medium">System</th>
              <th className="py-3 pr-4 font-medium">Crit.</th>
              <th className="py-3 pr-4 font-medium">Budget</th>
              <th className="py-3 pr-4 font-medium">Package</th>
              <th className="py-3 pr-4 font-medium">Status</th>
              <th className="py-3 pr-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr data-testid="admin-no-audit">
                <td colSpan={9} className="py-10 text-center text-sm text-muted">No audit requests yet.</td>
              </tr>
            ) : (
              items.map((it, i) => (
                <Fragment key={it.id || i}>
                  <tr className="border-t border-[var(--line)] align-top" data-testid={`audit-row-${i}`}>
                    <td className="py-3 pr-4 text-xs text-muted whitespace-nowrap">{it.created_at ? new Date(it.created_at).toLocaleDateString() : "-"}</td>
                    <td className="py-3 pr-4 text-sm text-white font-medium whitespace-nowrap">
                      {it.name}
                      <span className="block text-xs text-muted font-normal">{it.company}</span>
                    </td>
                    <td className="py-3 pr-4 text-sm text-[var(--blue-bright)]"><a href={`mailto:${it.email}`} className="hover:underline">{it.email}</a></td>
                    <td className="py-3 pr-4 text-xs text-white/90 whitespace-nowrap">{SYS_LABEL[it.system_type] || it.system_type}</td>
                    <td className="py-3 pr-4 text-xs text-white/90 whitespace-nowrap">{CRIT_LABEL[it.business_criticality] || it.business_criticality}</td>
                    <td className="py-3 pr-4 text-xs text-white/90 whitespace-nowrap">{BUDGET_LABEL[it.budget_range] || it.budget_range}</td>
                    <td className="py-3 pr-4 text-xs text-white/90 whitespace-nowrap">{it.package_interest}</td>
                    <td className="py-3 pr-4">
                      <select
                        value={it.status}
                        onChange={(e) => patch(it.id, { status: e.target.value })}
                        disabled={busyId === it.id}
                        className={`rounded-lg bg-[rgba(8,12,22,0.7)] border px-2 py-1.5 text-xs font-mono outline-none cursor-pointer ${STATUS_TONE[it.status] || ""}`}
                        data-testid={`audit-status-${i}`}
                      >
                        {AUDIT_STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-[#0b111e] text-white">{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 pr-2 whitespace-nowrap">
                      <button onClick={() => { setOpenId(openId === it.id ? null : it.id); setNoteDraft(""); }} className="btn-ghost !py-1.5 !px-2.5 text-xs" data-testid={`audit-details-${i}`}>
                        Details <ChevronDown size={13} className={openId === it.id ? "rotate-180" : ""} />
                      </button>
                    </td>
                  </tr>
                  {openId === it.id && (
                    <tr className="border-t border-[var(--line)] bg-white/[0.02]" data-testid={`audit-detail-${i}`}>
                      <td colSpan={9} className="p-5">
                        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
                          <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)] mb-2">Message</div>
                            <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">{it.message}</p>
                            <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted">
                              {it.phone ? <span>Phone: <span className="text-white/90">{it.phone}</span></span> : null}
                              {it.source_cta ? <span>Source: <span className="text-white/90">{it.source_cta}</span></span> : null}
                              <span>Updated: <span className="text-white/90">{it.updated_at ? new Date(it.updated_at).toLocaleString() : "-"}</span></span>
                            </div>
                          </div>
                          <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)] mb-2">Internal notes</div>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {(it.internal_notes || []).length === 0 ? (
                                <p className="text-xs text-muted">No notes yet.</p>
                              ) : (
                                it.internal_notes.map((n, ni) => (
                                  <div key={ni} className="rounded-lg border border-[var(--line)] p-2.5">
                                    <p className="text-sm text-white/90">{n.note}</p>
                                    <span className="text-[10px] text-muted font-mono">{n.at ? new Date(n.at).toLocaleString() : ""}</span>
                                  </div>
                                ))
                              )}
                            </div>
                            <div className="mt-3 flex gap-2">
                              <input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Add internal note..." className={`${fieldClass} !py-2 text-xs`} data-testid={`audit-note-input-${i}`} />
                              <button onClick={() => addNote(it.id)} disabled={busyId === it.id || !noteDraft.trim()} className="btn-ghost !py-2 !px-3 text-xs shrink-0 disabled:opacity-50" data-testid={`audit-note-save-${i}`}>Add</button>
                            </div>
                            <button onClick={() => remove(it.id)} disabled={busyId === it.id} className="mt-4 inline-flex items-center gap-1.5 text-xs text-[var(--red)] hover:underline disabled:opacity-50" data-testid={`audit-delete-${i}`}>
                              <Trash2 size={13} /> Delete / archive
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const BOOKING_STATUSES = ["booked", "completed", "cancelled"];
const MEETING_LABEL = { phone: "Phone", zoom: "Zoom", teams: "Teams" };

const Bookings = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data || []);
    } catch (e) {
      /* handled by parent session check */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers });
      setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
    } catch (e) {
      /* ignore */
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers });
      setItems((arr) => arr.filter((it) => it.id !== id));
    } catch (e) {
      /* ignore */
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="glass-card rounded-3xl p-6 mt-5" data-testid="admin-bookings-section">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">Bookings ({items.length})</div>
        <button onClick={load} className="btn-ghost !py-2 !px-3 text-xs" data-testid="bookings-refresh-btn">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse" data-testid="admin-bookings-table">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">
              <th className="py-3 pr-4 font-medium whitespace-nowrap">Date</th>
              <th className="py-3 pr-4 font-medium whitespace-nowrap">Time</th>
              <th className="py-3 pr-4 font-medium">Name / Company</th>
              <th className="py-3 pr-4 font-medium">Email</th>
              <th className="py-3 pr-4 font-medium">Meeting</th>
              <th className="py-3 pr-4 font-medium">Status</th>
              <th className="py-3 pr-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr data-testid="admin-no-bookings">
                <td colSpan={7} className="py-10 text-center text-sm text-muted">No bookings yet.</td>
              </tr>
            ) : (
              items.map((it, i) => (
                <tr key={it.id || i} className={`border-t border-[var(--line)] align-top ${it.status === "cancelled" ? "opacity-50" : ""}`} data-testid={`booking-row-${i}`}>
                  <td className="py-3 pr-4 text-sm text-white font-medium whitespace-nowrap">{it.date}</td>
                  <td className="py-3 pr-4 text-sm text-[var(--blue-bright)] font-mono whitespace-nowrap">{it.time}</td>
                  <td className="py-3 pr-4 text-sm text-white whitespace-nowrap">
                    {it.name}
                    {it.company ? <span className="block text-xs text-muted font-normal">{it.company}</span> : null}
                    {it.phone ? <span className="block text-xs text-muted font-normal">{it.phone}</span> : null}
                  </td>
                  <td className="py-3 pr-4 text-sm text-[var(--blue-bright)]"><a href={`mailto:${it.email}`} className="hover:underline">{it.email}</a></td>
                  <td className="py-3 pr-4 text-xs text-white/90 whitespace-nowrap">{MEETING_LABEL[it.meeting_type] || it.meeting_type}</td>
                  <td className="py-3 pr-4">
                    <select
                      value={it.status}
                      onChange={(e) => setStatus(it.id, e.target.value)}
                      disabled={busyId === it.id}
                      className="rounded-lg bg-[rgba(8,12,22,0.7)] border border-[var(--line-strong)] px-2 py-1.5 text-xs font-mono outline-none cursor-pointer text-white"
                      data-testid={`booking-status-${i}`}
                    >
                      {BOOKING_STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#0b111e] text-white">{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 pr-2 whitespace-nowrap">
                    <button onClick={() => remove(it.id)} disabled={busyId === it.id} className="inline-flex items-center gap-1.5 text-xs text-[var(--red)] hover:underline disabled:opacity-50" data-testid={`booking-delete-${i}`}>
                      <Trash2 size={13} /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSubmissions([]);
    setStats(null);
  }, []);

  const loadData = useCallback(async (tk) => {
    setLoadingData(true);
    try {
      const headers = { Authorization: `Bearer ${tk}` };
      const [s, st] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setSubmissions(s.data || []);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        setError("Session expired. Please log in again.");
      }
    } finally {
      setLoadingData(false);
    }
  }, [logout]);

  useEffect(() => {
    document.documentElement.lang = "en";
    if (token) loadData(token);
  }, [token, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Login screen ----------------
  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--bg)] grid place-items-center px-5" data-testid="admin-login">
        <div className="noise" />
        <div className="glow-orb animate-glow pointer-events-none" style={{ width: 460, height: 460, top: "10%", left: "50%", transform: "translateX(-50%)", background: "var(--blue)", opacity: 0.22 }} />
        <div className="relative z-10 w-full max-w-md glass-card depth-card rounded-3xl p-8">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-xl panel text-[var(--blue-bright)]">
              <Lock size={20} />
            </span>
            <div>
              <div className="font-display font-semibold text-white">ReverseAI.dk Admin</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--muted-2)]">Secure Systems &amp; AI</div>
            </div>
          </div>
          <h1 className="mt-7 font-display text-2xl font-semibold text-white">Sign in</h1>
          <p className="mt-2 text-sm text-muted">Restricted area. Leads and CTA analytics.</p>

          <form onSubmit={login} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider">Email</label>
              <input type="email" className={fieldClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="admin-email-input" />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider">Password</label>
              <input type="password" className={fieldClass} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="********" data-testid="admin-password-input" />
            </div>
            {error && <p className="text-sm text-[var(--red)]" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-login-btn">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Signing in...</> : "Sign in"}
            </button>
          </form>
          <a href="/" className="mt-6 block text-center text-sm text-muted hover:text-white transition-colors" data-testid="admin-back-home">Back to site</a>
        </div>
      </div>
    );
  }

  // ---------------- Dashboard ----------------
  const chartData = [
    { name: "Book", value: stats?.clicks?.book ?? 0, fill: "var(--blue)" },
    { name: "Contact", value: stats?.clicks?.contact ?? 0, fill: "var(--green)" },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] relative" data-testid="admin-dashboard">
      <div className="noise" />
      <div className="relative z-10 mx-auto max-w-7xl px-5 py-10">
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-lg panel font-display font-bold text-sm text-white">RA</span>
            <div>
              <div className="font-display font-semibold text-white">Admin dashboard</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--muted-2)]">ReverseAI.dk</div>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={() => loadData(token)} className="btn-ghost !py-2.5 !px-4 text-sm" data-testid="admin-refresh-btn">
              <RefreshCw size={15} className={loadingData ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="btn-ghost !py-2.5 !px-4 text-sm" data-testid="admin-logout-btn">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </header>

        {/* Stat cards */}
        <div className="scene-3d mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard icon={CalendarClock} label="Upcoming bookings" value={stats?.bookings_upcoming ?? 0} tone="green" testId="stat-bookings" />
          <StatCard icon={CalendarCheck} label="Book vurdering clicks" value={stats?.clicks?.book ?? 0} tone="blue" testId="stat-book-clicks" />
          <StatCard icon={FileSearch} label="Audit requests" value={stats?.audit_requests ?? 0} tone="blue" testId="stat-audit-requests" />
          <StatCard icon={Inbox} label="Total leads" value={stats?.submissions ?? submissions.length} tone="green" testId="stat-total-leads" />
        </div>

        {/* Click chart */}
        <div className="glass-card rounded-3xl p-6 mt-5" data-testid="admin-click-chart">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)] mb-4">CTA clicks</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" stroke="#8b97b1" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis allowDecimals={false} stroke="#8b97b1" tickLine={false} axisLine={false} fontSize={12} />
                <Tooltip cursor={{ fill: "rgba(96,150,255,0.08)" }} contentStyle={{ background: "#0b111e", border: "1px solid rgba(96,150,255,0.32)", borderRadius: 12, color: "#eaf0fb" }} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={90}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Submissions table */}
        <div className="glass-card rounded-3xl p-6 mt-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">Submissions ({submissions.length})</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" data-testid="admin-submissions-table">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">
                  <th className="py-3 pr-4 font-medium">Name</th>
                  <th className="py-3 pr-4 font-medium">Email</th>
                  <th className="py-3 pr-4 font-medium">Message</th>
                  <th className="py-3 pr-4 font-medium whitespace-nowrap">Date</th>
                </tr>
              </thead>
              <tbody>
                {submissions.length === 0 ? (
                  <tr data-testid="admin-no-submissions">
                    <td colSpan={4} className="py-10 text-center text-sm text-muted">No submissions yet.</td>
                  </tr>
                ) : (
                  submissions.map((s, i) => (
                    <tr key={s.id || i} className="border-t border-[var(--line)] align-top" data-testid={`submission-row-${i}`}>
                      <td className="py-3 pr-4 text-sm text-white font-medium whitespace-nowrap">
                        {s.name}
                        {s.company ? <span className="block text-xs text-muted font-normal">{s.company}</span> : null}
                        {s.package ? <span className="mt-1 inline-block font-mono text-[10px] text-[var(--blue-bright)] border border-[var(--line-strong)] rounded px-1.5 py-0.5">{s.package}</span> : null}
                      </td>
                      <td className="py-3 pr-4 text-sm text-[var(--blue-bright)]">
                        <a href={`mailto:${s.email}`} className="hover:underline">{s.email}</a>
                      </td>
                      <td className="py-3 pr-4 text-sm text-white/90 leading-relaxed max-w-md whitespace-pre-wrap">{s.message}</td>
                      <td className="py-3 pr-4 text-xs text-muted whitespace-nowrap">{s.created_at ? new Date(s.created_at).toLocaleString() : "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Secure System Audit Requests pipeline */}
        <AuditRequests token={token} />

        {/* Native bookings */}
        <Bookings token={token} />
      </div>
    </div>
  );
};
